import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, MessageSquare, MessageCircle, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCmsContent } from "@/hooks/useCmsContent";
import { useContactInfo } from "@/hooks/useContactInfo";

const Contact = () => {
  const { t } = useLanguage();
  const { get } = useCmsContent();
  const contactInfo = useContactInfo();
  const cp = t.contactPage;
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", phone: "", subject: "", checkIn: "", checkOut: "", guests: "", message: "" });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleSelectChange = (name: string, value: string) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch("https://formspree.io/f/xvzbrzae", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast({ title: cp.toastTitle, description: cp.toastDesc });
        setFormData({ firstName: "", lastName: "", email: "", phone: "", subject: "", checkIn: "", checkOut: "", guests: "", message: "" });
      } else {
        toast({ title: "Fehler", description: "Nachricht konnte nicht gesendet werden.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Fehler", description: "Nachricht konnte nicht gesendet werden.", variant: "destructive" });
    }
    setIsSubmitting(false);
  };

  const subjects = [
    { value: "reservation", label: cp.subjects.reservation },
    { value: "restaurant", label: cp.subjects.restaurant },
    { value: "group", label: cp.subjects.group },
    { value: "event", label: cp.subjects.event },
    { value: "feedback", label: cp.subjects.feedback },
    { value: "other", label: cp.subjects.other },
  ];

  const email = contactInfo?.email || "info@esoshotelquelle.com";
  const phone = contactInfo?.phone || "+41 81 302 11 13";
  const address = contactInfo?.address || "Esos Hotel Quelle\nBadstrasse 9a\n7310 Bad Ragaz\nSt. Gallen, Schweiz";

  return (
    <section id="contact" className="hotel-section bg-card">
      <div className="hotel-container">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
          <p className="hotel-caption text-accent mb-4">{cp.caption}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light text-foreground mb-4 md:mb-6">{cp.heading}</h2>
          <p className="hotel-body text-muted-foreground">{cp.text}</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-10 md:mb-16">
          <a href={`tel:${phone.replace(/\s/g, "")}`} className="group bg-background p-4 md:p-6 rounded-lg shadow-soft hover:shadow-medium transition-all duration-300 text-center border border-transparent hover:border-accent/20">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-primary/15 transition-colors"><Phone className="w-5 h-5 text-primary" strokeWidth={1.5} /></div>
            <p className="font-display text-base md:text-lg font-semibold text-foreground mb-1">Reservation</p>
            <p className="font-body text-xs md:text-sm text-muted-foreground break-all">{phone}</p>
          </a>
          <a href="tel:+41772581052" className="group bg-background p-4 md:p-6 rounded-lg shadow-soft hover:shadow-medium transition-all duration-300 text-center border border-transparent hover:border-accent/20">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-primary/15 transition-colors"><Phone className="w-5 h-5 text-primary" strokeWidth={1.5} /></div>
            <p className="font-display text-base md:text-lg font-semibold text-foreground mb-1">{cp.mobile}</p>
            <p className="font-body text-xs md:text-sm text-muted-foreground break-all">+41 77 258 10 52</p>
          </a>
          <a href={`mailto:${email}`} className="group bg-background p-4 md:p-6 rounded-lg shadow-soft hover:shadow-medium transition-all duration-300 text-center border border-transparent hover:border-accent/20">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-primary/15 transition-colors"><Mail className="w-5 h-5 text-primary" strokeWidth={1.5} /></div>
            <p className="font-display text-base md:text-lg font-semibold text-foreground mb-1">{cp.email}</p>
            <p className="font-body text-xs md:text-sm text-muted-foreground break-all">{email}</p>
          </a>
          <a href="https://wa.me/41772581052" target="_blank" rel="noopener noreferrer" className="group bg-background p-4 md:p-6 rounded-lg shadow-soft hover:shadow-medium transition-all duration-300 text-center border border-transparent hover:border-accent/20">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-primary/15 transition-colors"><MessageCircle className="w-5 h-5 text-primary" strokeWidth={1.5} /></div>
            <p className="font-display text-base md:text-lg font-semibold text-foreground mb-1">{cp.whatsapp}</p>
            <p className="font-body text-xs md:text-sm text-muted-foreground">{cp.sendMessage}</p>
          </a>
        </div>
        <div className="grid lg:grid-cols-[1fr_380px] gap-8 lg:gap-16">
          <div id="contact-form" className="bg-background p-6 md:p-10 rounded-lg shadow-medium">
            <div className="flex items-center gap-3 mb-8"><MessageSquare className="w-6 h-6 text-accent" /><h3 className="font-display text-2xl font-semibold text-foreground">{cp.msgHeading}</h3></div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label htmlFor="ct-firstName" className="hotel-caption text-foreground block mb-2">{cp.firstName} *</label><Input id="ct-firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required /></div>
                <div><label htmlFor="ct-lastName" className="hotel-caption text-foreground block mb-2">{cp.lastName} *</label><Input id="ct-lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label htmlFor="ct-email" className="hotel-caption text-foreground block mb-2">{cp.email} *</label><Input id="ct-email" type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="email@example.com" required /></div>
                <div><label htmlFor="ct-phone" className="hotel-caption text-foreground block mb-2">{cp.phone}</label><Input id="ct-phone" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+41..." /></div>
              </div>
              <div>
                <label htmlFor="ct-subject" className="hotel-caption text-foreground block mb-2">{cp.subject} *</label>
                <Select value={formData.subject} onValueChange={(value) => handleSelectChange("subject", value)}>
                  <SelectTrigger id="ct-subject"><SelectValue placeholder={cp.selectSubject} /></SelectTrigger>
                  <SelectContent>{subjects.map((subj) => (<SelectItem key={subj.value} value={subj.value}>{subj.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              {(formData.subject === "reservation" || formData.subject === "group") && (
                <div className="grid md:grid-cols-3 gap-4">
                  <div><label htmlFor="ct-checkIn" className="hotel-caption text-foreground block mb-2">{cp.checkIn}</label><Input id="ct-checkIn" type="date" name="checkIn" value={formData.checkIn} onChange={handleInputChange} /></div>
                  <div><label htmlFor="ct-checkOut" className="hotel-caption text-foreground block mb-2">{cp.checkOut}</label><Input id="ct-checkOut" type="date" name="checkOut" value={formData.checkOut} onChange={handleInputChange} /></div>
                  <div>
                    <label htmlFor="ct-guests" className="hotel-caption text-foreground block mb-2">{cp.guests}</label>
                    <Select value={formData.guests} onValueChange={(value) => handleSelectChange("guests", value)}>
                      <SelectTrigger id="ct-guests"><SelectValue placeholder={cp.count} /></SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (<SelectItem key={num} value={num.toString()}>{num} {num === 1 ? cp.person : cp.persons}</SelectItem>))}
                        <SelectItem value="10+">10+ {cp.persons}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div><label htmlFor="ct-message" className="hotel-caption text-foreground block mb-2">{cp.message} *</label><Textarea id="ct-message" name="message" value={formData.message} onChange={handleInputChange} placeholder={cp.messagePlaceholder} className="min-h-[120px]" required /></div>
              <Button type="submit" variant="hotel" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? cp.sending : (<><Send className="w-4 h-4 mr-2" />{cp.send}</>)}
              </Button>
              <p className="font-body text-xs text-muted-foreground text-center">{cp.privacyText}{" "}<a href="/datenschutz" className="text-accent hover:underline">{cp.privacyLink}</a>.</p>
            </form>
          </div>
          <div className="space-y-6">
            <div className="bg-background rounded-lg overflow-hidden shadow-soft">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-accent/10 rounded-lg"><MapPin className="w-5 h-5 text-accent" /></div><h4 className="font-display text-lg font-semibold text-foreground">{cp.address}</h4></div>
                <p className="font-body text-sm text-muted-foreground leading-relaxed mb-4 whitespace-pre-line">{address}</p>
                <Button variant="outline" size="sm" className="w-full" asChild><a href={contactInfo?.maps_url || "https://maps.google.com/?q=Esos+Hotel+Quelle+Badstrasse+9a+Bad+Ragaz"} target="_blank" rel="noopener noreferrer"><MapPin className="w-4 h-4 mr-2" />{cp.openInMaps}</a></Button>
              </div>
            </div>
            <div className="bg-background p-6 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-accent/10 rounded-lg"><Clock className="w-5 h-5 text-accent" /></div><h4 className="font-display text-lg font-semibold text-foreground">{cp.receptionTitle}</h4></div>
              <div className="space-y-2">
                <div className="flex justify-between font-body text-sm"><span className="text-muted-foreground">{cp.receptionHours}</span><span className="text-foreground font-medium">{cp.receptionValue}</span></div>
                <div className="w-full h-px bg-border" />
                <div className="flex justify-between font-body text-sm"><span className="text-muted-foreground">Check-in</span><span className="text-foreground font-medium">{cp.checkInTime}</span></div>
                <div className="w-full h-px bg-border" />
                <div className="flex justify-between font-body text-sm"><span className="text-muted-foreground">Check-out</span><span className="text-foreground font-medium">{cp.checkOutTime}</span></div>
              </div>
            </div>
            <div className="bg-background p-6 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-accent/10 rounded-lg"><CreditCard className="w-5 h-5 text-accent" /></div><h4 className="font-display text-lg font-semibold text-foreground">{cp.paymentTitle}</h4></div>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">{cp.paymentMethods}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
